'use client';

import { useState, useEffect, useMemo } from 'react';
import { useRouter } from 'next/navigation';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Video, Copy, Check } from 'lucide-react';
import { useSupabaseAuth } from '@/lib/auth/simplified/SupabaseAuthContext';
import { toast } from 'sonner';
import { fallbackCopyToClipboard } from '@/lib/utils/clipboard';
import { getRoomUrl, getRoomName } from '@/lib/utils/room-url';
import type { UserDomainInfo } from '@/lib/utils/room-url';

interface QuickRoomAccessProps {
  isCollapsed: boolean;
  className?: string;
  activeDomain?: { domain_id: string; domain_handle: string; role: string } | null;
}

interface RoomStatus {
  isActive: boolean;
  participantCount: number;
  isRecording: boolean;
  startedAt?: string;
}

export function QuickRoomAccess({ isCollapsed, className, activeDomain }: QuickRoomAccessProps) {
  const router = useRouter();
  const { profile } = useSupabaseAuth();
  const [isHovered, setIsHovered] = useState(false);
  const [copied, setCopied] = useState(false);
  const [roomStatus, setRoomStatus] = useState<RoomStatus | null>(null);

  const handle = profile?.handle;

  // Memoize domainInfo so the object identity only changes when the underlying
  // activeDomain fields change. Previously this was recreated on every render,
  // which (because it appeared in the polling effect deps below) tore down and
  // recreated the status-poll interval on every render — firing the fetch
  // immediately each time. The result was dozens of requests per second to
  // /api/rooms/<handle>/status during any render-heavy parent update.
  const domainInfo: UserDomainInfo | null = useMemo(
    () => activeDomain ? {
      domain_id: activeDomain.domain_id,
      domain_handle: activeDomain.domain_handle,
      role: activeDomain.role,
    } : null,
    [activeDomain?.domain_id, activeDomain?.domain_handle, activeDomain?.role]
  );

  const isTeamContext = domainInfo && domainInfo.role !== 'owner' && domainInfo.role !== 'byline';
  const roomPath = handle ? getRoomUrl(handle, domainInfo, profile?.domain_id || null) : null;
  const roomUrl = handle && roomPath ? `${typeof window !== 'undefined' ? window.location.origin : ''}${roomPath}` : null;
  const effectiveRoomName = handle ? getRoomName(handle, domainInfo, profile?.domain_id || null) : null;

  // Fetch room status (poll every 30 seconds). Keep deps on stable primitives
  // so the interval only resets when the actual room identity changes.
  const domainHandleForStatus = isTeamContext && domainInfo ? domainInfo.domain_handle : null;
  useEffect(() => {
    if (!handle) return;

    const fetchStatus = async () => {
      try {
        const statusUrl = domainHandleForStatus
          ? `/api/rooms/${handle}/status?domain=${domainHandleForStatus}`
          : `/api/rooms/${handle}/status`;
        const res = await fetch(statusUrl);
        if (res.ok) {
          const data = await res.json();
          setRoomStatus(data);
        }
      } catch (err) {
        console.error('[QuickRoomAccess] Failed to fetch room status:', err);
      }
    };

    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [handle, domainHandleForStatus]);

  const handleNavigate = () => {
    if (roomPath) {
      router.push(roomPath);
    }
  };

  // Copy room link to clipboard
  const handleCopy = async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent navigation

    if (!roomUrl) return;

    try {
      await navigator.clipboard.writeText(roomUrl);
      setCopied(true);
      toast.success('Room link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const success = fallbackCopyToClipboard(roomUrl);
      if (success) {
        setCopied(true);
        toast.success('Room link copied!');
        setTimeout(() => setCopied(false), 2000);
      } else {
        toast.error('Failed to copy link');
      }
    }
  };

  // Hide component if user not authenticated or no handle
  if (!profile || !handle) {
    return null;
  }

  // Show green dot if room is active with more than 1 participant (owner + others)
  const showIndicator = roomStatus?.isActive && roomStatus.participantCount > 1;

  // Collapsed state
  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleNavigate}
            className={cn('relative h-10 w-10 rounded-lg hover:bg-muted', className)}
            aria-label={isTeamContext ? "Team Conversation Room" : "My Conversation Room"}
          >
            <Video className="h-5 w-5" />

            {/* Green dot indicator */}
            {showIndicator && (
              <div
                className="absolute top-1 right-1 h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse ring-2 ring-background"
                aria-label={`${roomStatus.participantCount} participants active`}
              />
            )}
          </Button>
        </TooltipTrigger>
        <TooltipContent side="right">
          <div className="text-sm">
            <p className="font-medium">{isTeamContext ? "Team Room" : "My Conversation Room"}</p>
            {showIndicator && (
              <p className="text-xs text-muted-foreground">
                {roomStatus.participantCount} participant{roomStatus.participantCount !== 1 ? 's' : ''} active
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Expanded state
  return (
    <div
      className={cn('relative', className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Button
        variant="ghost"
        onClick={handleNavigate}
        className="w-full justify-start gap-3 h-10 relative"
        aria-label={isTeamContext ? "Team Conversation Room" : "My Conversation Room"}
      >
        <div className="relative">
          <Video className="h-5 w-5 shrink-0" />

          {/* Green dot indicator */}
          {showIndicator && (
            <div
              className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse ring-2 ring-background"
              aria-label={`${roomStatus.participantCount} participants active`}
            />
          )}
        </div>

        <span className="font-medium flex-1 text-left">{isTeamContext ? "Team Room" : "My Room"}</span>

        {/* Copy button on hover */}
        {isHovered && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleCopy}
            className={cn(
              'h-7 w-7 shrink-0 transition-colors',
              copied && 'text-green-600'
            )}
            aria-label={copied ? 'Link copied' : 'Copy room link'}
          >
            {copied ? (
              <Check className="h-4 w-4" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
          </Button>
        )}
      </Button>
    </div>
  );
}
